/**
 * Quote Modal Handler
 * Handles the quote request modal functionality including:
 * - Opening/closing modal
 * - Form validation
 * - Form submission via Shopify contact form
 * - Success message display with auto-close
 */

class QuoteModal {
constructor() {
    this.modal = document.getElementById('quote-modal');
    if (!this.modal) return;

    this.form = this.modal.querySelector('#quote-contact-form');
    this.formContainer = this.modal.querySelector('[data-quote-form-container]');
    this.submitButton = this.modal.querySelector('[data-quote-submit]');
    this.submitText = this.modal.querySelector('[data-submit-text]');
    this.spinner = this.modal.querySelector('[data-quote-spinner]');

    // Message elements
    this.successMessage = this.modal.querySelector('[data-quote-success-message]');
    this.errorMessage = this.modal.querySelector('[data-quote-error-message]');
    this.errorText = this.modal.querySelector('[data-error-text]');

    // Form fields
    this.productInput = this.modal.querySelector('[data-quote-product-input]');
    this.phoneInput = this.modal.querySelector('[data-quote-phone]');
    this.emailInput = this.modal.querySelector('[data-quote-email]');
    this.messageInput = this.modal.querySelector('[data-quote-message]');
    this.gdprCheckbox = this.modal.querySelector('[data-quote-gdpr]');
    this.bodyField = this.modal.querySelector('[data-quote-body]');
    this.subjectField = this.modal.querySelector('[data-quote-subject]');

    // Field error elements
    this.phoneError = this.modal.querySelector('[data-error-phone]');
    this.emailError = this.modal.querySelector('[data-error-email]');
    this.gdprError = this.modal.querySelector('[data-error-gdpr]');

    this.autoCloseTimer = null;
    this.isSubmitting = false;

    this.init();
  }

  init() {
    // Bind event listeners
    this.bindQuoteButtons();
    this.bindCloseButtons();
    this.bindFormEvents();
    this.bindEscapeKey();
  }

  bindQuoteButtons() {
    const quoteButtons = document.querySelectorAll('[data-quote-button]');
    quoteButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const productTitle = button.getAttribute('data-product-title');
        const productUrl = button.getAttribute('data-product-url');
        this.openModal(productTitle, productUrl);
      });
    });
  }

  bindCloseButtons() {
    const closeButtons = this.modal.querySelectorAll('[data-quote-modal-close]');
    closeButtons.forEach(button => {
      button.addEventListener('click', () => this.closeModal());
    });
  }

bindFormEvents() {
    if (!this.form) return;

    // Form submission - validate then allow normal submission
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Validate form first
      if (!this.validateForm()) {
        return;
      }

      // Update body field with all information before submission
      if (this.bodyField && this.phoneInput && this.emailInput) {
        const productInfo = this.productInput?.value || '';
        const phone = this.phoneInput.value;
        const email = this.emailInput.value;
        const message = this.messageInput?.value.trim() || '';

        let bodyText = `Produktförfrågan: ${productInfo}\n\nKontaktinformation:\nTelefon: ${phone}\nE-post: ${email}`;

        if (message) {
          bodyText += `\n\nYtterligare information:\n${message}`;
        }

        bodyText += `\n\nKunden har godkänt GDPR-villkor för att bli kontaktad.`;
        this.bodyField.value = bodyText;
      }

      // Submit to hidden iframe to avoid page reload
      this.submitToIframe();
    }, true);

    // Real-time validation - only clear errors on input, don't validate on blur
    if (this.phoneInput) {
      this.phoneInput.addEventListener('input', () => this.clearError('phone'));
    }

    if (this.emailInput) {
      this.emailInput.addEventListener('input', () => this.clearError('email'));
    }

    if (this.gdprCheckbox) {
      this.gdprCheckbox.addEventListener('change', () => this.clearError('gdpr'));
    }
  }

  bindEscapeKey() {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.modal.hidden) {
        this.closeModal();
      }
    });
  }

openModal(productTitle, productUrl) {
    // Hide all messages
    this.hideMessages();

    // Store product info for later use
    this.currentProduct = {
      title: productTitle,
      url: productUrl
    };

    // Pre-fill product information
    if (this.productInput) {
      this.productInput.value = productTitle;
    }

    // Update subject with product name
    if (this.subjectField) {
      this.subjectField.value = `Förfrågan om offert - ${productTitle}`;
    }

    // Show modal
    this.modal.hidden = false;
    document.body.classList.add('quote-modal-open');

    // Focus first input
    setTimeout(() => {
      if (this.phoneInput) {
        this.phoneInput.focus();
      }
    }, 100);
  }

  closeModal() {
    // Clear any auto-close timer
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }

    // Hide modal
    this.modal.hidden = true;
    document.body.classList.remove('quote-modal-open');

    // Reset form after animation
    setTimeout(() => {
      this.resetForm();
    }, 300);
  }

resetForm() {
    if (this.form) {
      this.form.reset();
    }

    // Reset UI states
    this.clearAllErrors();
    this.hideMessages();

    // Reset submit button
    this.setSubmitState(false);
  }

  validatePhone() {
    const phone = this.phoneInput?.value.trim();
    if (!phone) {
      this.showError('phone', 'Vänligen ange ett telefonnummer');
      return false;
    }
    this.clearError('phone');
    return true;
  }

  validateEmail() {
    const email = this.emailInput?.value.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email) {
      this.showError('email', 'Vänligen ange en e-postadress');
      return false;
    }

    if (!emailRegex.test(email)) {
      this.showError('email', 'Vänligen ange en giltig e-postadress');
      return false;
    }

    this.clearError('email');
    return true;
  }

  validateGdpr() {
    if (!this.gdprCheckbox?.checked) {
      this.showError('gdpr', 'Du måste godkänna villkoren');
      return false;
    }
    this.clearError('gdpr');
    return true;
  }

  validateForm() {
    const isPhoneValid = this.validatePhone();
    const isEmailValid = this.validateEmail();
    const isGdprValid = this.validateGdpr();

    return isPhoneValid && isEmailValid && isGdprValid;
  }

  showError(field, message) {
    const errorElement = this[`${field}Error`];
    const inputElement = this[`${field}Input`] || this[`${field}Checkbox`];

    if (errorElement) {
      errorElement.textContent = message;
      errorElement.hidden = false;
    }

    if (inputElement) {
      inputElement.classList.add('error');
    }
  }

  clearError(field) {
    const errorElement = this[`${field}Error`];
    const inputElement = this[`${field}Input`] || this[`${field}Checkbox`];

    if (errorElement) {
      errorElement.hidden = true;
    }

    if (inputElement) {
      inputElement.classList.remove('error');
    }
  }

  clearAllErrors() {
    this.clearError('phone');
    this.clearError('email');
    this.clearError('gdpr');
  }

  setSubmitState(isSubmitting) {
    this.isSubmitting = isSubmitting;

    if (this.submitButton) {
      this.submitButton.disabled = isSubmitting;
    }

    if (this.submitText) {
      this.submitText.textContent = isSubmitting ? 'Skickar...' : 'Jag vill bli kontaktad';
    }

    if (this.spinner) {
      this.spinner.hidden = !isSubmitting;
    }

    // Hide/show icon
    const icon = this.submitButton?.querySelector('.quote-modal__submit-icon');
    if (icon) {
      icon.style.display = isSubmitting ? 'none' : 'block';
    }
  }

submitToIframe() {
    // Prevent double submission
    if (this.isSubmitting) return;

    // Hide any previous messages
    this.hideMessages();

    // Set submitting state
    this.setSubmitState(true);

    // Create or get hidden iframe
    let iframe = document.getElementById('quote-form-iframe');
    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.id = 'quote-form-iframe';
      iframe.name = 'quote-form-iframe';
      iframe.style.display = 'none';
      document.body.appendChild(iframe);
    }

    // Set form target to iframe
    this.form.setAttribute('target', 'quote-form-iframe');

    // Listen for iframe load (form submission complete)
    iframe.onload = () => {
      try {
        // Try to read iframe content to detect success/error
        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
        const iframeContent = iframeDoc.body.innerHTML.toLowerCase();

        // Check for success indicators (Shopify redirects to thank you page or shows success message)
        const isSuccess = iframeContent.includes('thank') ||
                          iframeContent.includes('tack') ||
                          iframeContent.includes('success') ||
                          !iframeContent.includes('error');

        if (isSuccess && iframeContent.length > 0) {
          // Show success message
          this.showSuccessMessage();
          // Clear form fields after successful submission
          setTimeout(() => {
            if (this.form) {
              this.form.reset();
            }
            this.clearAllErrors();
          }, 500);
        } else if (iframeContent.includes('error') || iframeContent.includes('captcha')) {
          throw new Error('Form validation error');
        }
      } catch (error) {
        // Cross-origin restrictions - assume success if no error thrown during submission
        console.log('Form submitted (cross-origin response)');
        // Show success message anyway since form was submitted
        this.showSuccessMessage();
        setTimeout(() => {
          if (this.form) {
            this.form.reset();
          }
          this.clearAllErrors();
        }, 500);
      } finally {
        this.setSubmitState(false);
      }
    };

    // Submit the form
    this.form.submit();
  }

showSuccessMessage() {
    // Show success message
    if (this.successMessage) {
      this.successMessage.style.display = 'flex';
    }

    // Scroll to message
    this.scrollToMessage();

    // User can close manually - no auto-close
  }

  showErrorMessage(message = 'Ett fel uppstod. Vänligen försök igen.') {
    // Update error text if provided
    if (this.errorText && message) {
      this.errorText.textContent = message;
    }

    // Show error message
    if (this.errorMessage) {
      this.errorMessage.style.display = 'flex';
    }

    // Scroll to message
    this.scrollToMessage();

    // User can close manually - no auto-close
  }

hideMessages() {
    if (this.successMessage) {
      this.successMessage.style.display = 'none';
    }
    if (this.errorMessage) {
      this.errorMessage.style.display = 'none';
    }
  }

  scrollToMessage() {
    // Scroll the modal content to show the message
    setTimeout(() => {
      const modalContent = this.modal.querySelector('.quote-modal__content');
      if (modalContent) {
        modalContent.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    }, 100);
  }

}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new QuoteModal();
  });
} else {
  new QuoteModal();
}
